
// import { v4 as uuidv4 } from "uuid";
import { FloatingFeature } from "@models";
import { 
  AbilityEffect,
  ConsumedResource,
} from "@dndModels";
import { UpgradableNumber } from "@dndModels/misc/UpgradableNumber";
import { AttackType, CastingTime, DurationType, EffectType } from "@dndModels/Types";
import { STANDARD_CASTING_TIMES } from "@dndModels/Constants";


/**
 * These are spells and other abilities.  
 * I wanted to call them Abilities to make them more generic,
 * because I want to use this for things that aren't spells
 * (like Battlemaster Maneuvers, Weapon Attacks, Sneak Attack, etc).
 * 
 * bonus is a string, but it's complicated, so I'm going to 
 * define how it works here.
 * To help me make sure I understand it myself, I'm going to start
 * with some examples:
 * 
 * Weapon(die, bonus): 1 * {die} + {STR} + {bonus}
 * Range(die, bonus): 1 * {die} + {DEX} + {bonus}
 * Finesse(die, bonus): 1 * {die} + Max({STR},{DEX}) + {bonus}
 * SecondWeapon(die, bonus): 1 * {die} + {bonus}
 * SneakAttack(): Ceil({ClassLevel}/2) * {d6} 
 * 
 * EldritchBlast(): {d10} + ({Features[EldritchInvocations]}.includes("Agonizing Blast") ? {CHA} : 0)
 * Hex(): {d6}
 * BurningHands(slotLevel): (2 + {slotLevel}) * {d6}
 * CureWounds(slotLevel): {slotLevel} * {d8} + {SPM}
 * Note: Multiplier on a die roll doesn't mean roll once 
 * and then multiply.  
 * It means roll that many of it and sum.
 * Spells will each need their own formula.
 * And some will need two formulae, like Ice Knife.
 * Also Versatile weapons will pass different die based on 
 * one and two handed strikes.
 * 
 */
export class Ability extends FloatingFeature {
  parent_type = "";
  parent_id = "";
  
  // saving_throw_ability_score: string = ""; // Ability Score saving throw the target(s) have to make
  effects: AbilityEffect[] = []; // Formula for how much damage/healing to do
  // effect_2: AbilityEffect; // Some abilities have a second (like Ice Knife or Booming Blade or things that do different types of damage)
  range = ""; // Self, Touch, or a number
  range_2 = ""; // For some there are multiple ranges.  It can be an AoE size, or sometimes something else.
  concentration = false;
  notes = "";
  duration: DurationType = "Instantaneous";
  components: string[] = []; // VSM
  material_component = "";
  sight = false;
  casting_time: CastingTime = "A"; // A, BA, RA, X minute(s), etc
  special_casting_time = "";

  consumed_resource: ConsumedResource = new ConsumedResource();

  // resource_consumed: string = "None"; // Slot-X, Ki, Lay on Hands, Charge, etc.
  // amount_consumed: number = 0;
  // recharge_die: 4 | 6 | 8 | 10 | 12 | 20 | 100 = 6;
  // recharge_on: number[] = [];
  // slot_level: SpellLevel = 1; // If it consumes slots then this is the minimum level of the slot
  // slot_type: string = ""; // If it consumes a specific type of slot (usually Pact) then this holds that
  // special_resource_amount: UpgradableNumber = new UpgradableNumber();
  // special_resource_refresh_rule: string = ""; // Short Rest, Long Rest, Dawn, 1 Hour, 8 Hours, 24 Hours
  
  attack_bonus: UpgradableNumber = new UpgradableNumber();
  dc: UpgradableNumber = new UpgradableNumber();
  special_action_type: "Attack" | "Ability" | "Spell" | "Item Action" | "None" | "Inherit" = "Inherit";
  counts_as_weapon = "";
  weapon_properties: { wk_ids: string[], wmp_id: string } = { wk_ids: [], wmp_id: "" };
  show_in: ("Actions" | "Inventory" | "Item" | "Source")[] = ["Actions"];
  // spellcasting_ability: AbilityScoreExpandedType = "None";
  
  constructor(obj?: any) {
    super(obj);
    if (obj) {
      // if (obj.name === "Breath of the Dragon") {
      //   debugger;
      // }
      const saving_throw_ability_score = obj.saving_throw_ability_score !== "undefined" ? obj.saving_throw_ability_score : "";
      if (obj.effects && obj.effects.length) {
        obj.effects.forEach((effect: any) => {
          const new_effect = new AbilityEffect(effect);
          if (saving_throw_ability_score && !effect.saving_throw_ability_score) {
            new_effect.saving_throw_ability_score = saving_throw_ability_score;
          }
          this.effects.push(new_effect); // , this.name === "Steel Defender"));
        });
      } else if (obj.effect) {
        const new_effect = new AbilityEffect(obj.effect);
        if (saving_throw_ability_score && !obj.effect.saving_throw_ability_score) {
          new_effect.saving_throw_ability_score = saving_throw_ability_score;
        }
        this.effects.push(new_effect);
      }
      this.range = obj.range ? obj.range : "";
      this.range_2 = obj.range ? obj.range_2 : "";
      this.concentration = obj.concentration ? obj.concentration : false;
      this.notes = obj.notes ? obj.notes : "";
      this.duration = obj.duration ? obj.duration : "Instantaneous";
      this.components = obj.components ? [...obj.components] : [];
      this.material_component = obj.material_component !== "undefined" ? obj.material_component : "";
      this.sight = obj.sight ? obj.sight : false;
      if (obj && obj.sight === undefined) {
        this.sight = this.description.toLocaleLowerCase().includes("sight") || this.description.toLocaleLowerCase().includes("see");
      }
      this.casting_time = obj.casting_time ? obj.casting_time : "A";
      this.consumed_resource = obj.consumed_resource ? new ConsumedResource(obj.consumed_resource) : new ConsumedResource();
      if (!obj.consumed_resource) {
        this.consumed_resource.resource_consumed = obj.resource_consumed ? obj.resource_consumed : "None";
        this.consumed_resource.amount_consumed = obj.amount_consumed ? +obj.amount_consumed : 0;
        this.consumed_resource.slot_level = obj.slot_level ? obj.slot_level : 1;
        this.consumed_resource.slot_type = obj.slot_type ? obj.slot_type : "";
        if (obj.special_resource_amount && obj.special_resource_amount.base === undefined) {
          // Translate old set up to new
          this.consumed_resource.special_resource_amount = new UpgradableNumber();
          this.consumed_resource.special_resource_amount.base = +obj.special_resource_amount;
        } else if (obj && obj.special_resource_amount) {
          this.consumed_resource.special_resource_amount = new UpgradableNumber(obj.special_resource_amount);
        } else {
          this.consumed_resource.special_resource_amount = new UpgradableNumber();
        }
        this.consumed_resource.special_resource_refresh_rule = obj.special_resource_refresh_rule ? obj.special_resource_refresh_rule : "";
      }
      // if (this.name.includes("Fire Finger Gun")) {
      //   debugger;
      // }
      if (obj && obj.attack_bonus && obj.attack_bonus.base === undefined) {
        // Translate old set up to new
        this.attack_bonus = new UpgradableNumber();
        this.attack_bonus.base = +obj.attack_bonus;
      } else if (obj && obj.attack_bonus) {
        this.attack_bonus = new UpgradableNumber(obj.attack_bonus);
      } else {
        this.attack_bonus = new UpgradableNumber();
      }
      if (obj && obj.dc && obj.dc.base === undefined) {
        // Translate old set up to new
        this.dc = new UpgradableNumber();
        this.dc.base = +obj.dc;
      } else if (obj && obj.dc) {
        this.dc = new UpgradableNumber(obj.dc);
      } else {
        this.dc = new UpgradableNumber();
      }
      // }
      if (this.range === "undefined") {
        this.range = "";
      }
      if (this.range_2 === "undefined") {
        this.range_2 = "";
      }
      this.special_action_type = obj.special_action_type ? obj.special_action_type : "Inherit";
      this.counts_as_weapon = obj.counts_as_weapon ? obj.counts_as_weapon : "";
      this.weapon_properties = obj.weapon_properties ? obj.weapon_properties : { wk_ids: [], wmp_id: "" };
      this.show_in = obj.show_in ? obj.show_in : ["Actions"];
      this.special_casting_time = obj.special_casting_time ? obj.special_casting_time : "";
      if (!STANDARD_CASTING_TIMES.includes(this.casting_time)) {
        this.special_casting_time = this.casting_time;
        this.casting_time = "Special";
      }
      if (!obj.consumed_resource) {
        this.consumed_resource.recharge_die = obj.recharge_die ? obj.recharge_die : 6;
        this.consumed_resource.recharge_on = obj.recharge_on ? obj.recharge_on : [];
      }
      if (this.consumed_resource.resource_consumed === "Recharge" && this.consumed_resource.recharge_on.length === 0) {
        if (this.name.includes("(Recharge 5-6)")) {
          this.consumed_resource.recharge_on = [5,6];
          this.consumed_resource.resource_consumed = "Recharge";
        } else if (this.name.includes("(Recharge 4-6)")) {
          this.consumed_resource.recharge_on = [4,5,6];
          this.consumed_resource.resource_consumed = "Recharge";
        } else if (this.name.includes("(Recharge 6)")) {
          this.consumed_resource.recharge_on = [6];
          this.consumed_resource.resource_consumed = "Recharge";
        }
      }
      // if (this.description.includes("half as much")) {
      //   for (const effect of this.effects) {
      //     if (effect.type === "Damage") {
      //       effect.half_on_success = true;
      //     }
      //   }
      // }
      // this.spellcasting_ability = obj.spellcasting_ability ? obj.spellcasting_ability : "None";
    }
  }

  // load(obj: any): void {
  //   super.load(obj);
  //   if (obj) {
  //     const saving_throw_ability_score = obj.saving_throw_ability_score !== "undefined" ? obj.saving_throw_ability_score : "";
  //     if (obj.effects && obj.effects.length) {
  //       obj.effects.forEach((effect: any) => {
  //         const new_effect = new AbilityEffect(effect);
  //         if (saving_throw_ability_score && !effect.saving_throw_ability_score) {
  //           new_effect.saving_throw_ability_score = saving_throw_ability_score;
  //         }
  //         this.effects.push(new_effect); // , this.name === "Steel Defender"));
  //       });
  //     } else if (obj.effect) {
  //       const new_effect = new AbilityEffect(obj.effect);
  //       if (saving_throw_ability_score && !obj.effect.saving_throw_ability_score) {
  //         new_effect.saving_throw_ability_score = saving_throw_ability_score;
  //       }
  //       this.effects.push(new_effect);
  //     }
  //     // this.effect = obj ? new AbilityEffect(obj.effect) : new AbilityEffect();
  //     // this.effect_2 = obj ? new AbilityEffect(obj.effect_2) : new AbilityEffect();
  //     this.range = obj.range ? obj.range : "";
  //     this.range_2 = obj.range ? obj.range_2 : "";
  //     this.concentration = obj.concentration ? obj.concentration : false;
  //     this.notes = obj.notes ? obj.notes : "";
  //     this.duration = obj.duration ? obj.duration : "Instantaneous";
  //     this.components = obj.components ? [...obj.components] : [];
  //     this.material_component = obj.material_component !== "undefined" ? obj.material_component : "";
  //     this.sight = obj.sight ? obj.sight : false;
  //     if (obj && obj.sight === undefined) {
  //       this.sight = this.description.toLocaleLowerCase().includes("sight") || this.description.toLocaleLowerCase().includes("see");
  //     }
  //     this.casting_time = obj.casting_time ? obj.casting_time : "A";
  //     this.consumed_resource = obj.consumed_resource ? new ConsumedResource(obj.consumed_resource) : new ConsumedResource();
  //     if (!obj.consumed_resource) {
  //       this.consumed_resource.resource_consumed = obj.resource_consumed ? obj.resource_consumed : "None";
  //       this.consumed_resource.amount_consumed = obj.amount_consumed ? +obj.amount_consumed : 0;
  //       this.consumed_resource.slot_level = obj.slot_level ? obj.slot_level : 1;
  //       this.consumed_resource.slot_type = obj.slot_type ? obj.slot_type : "";
  //       if (obj.special_resource_amount && obj.special_resource_amount.base === undefined) {
  //         // Translate old set up to new
  //         this.consumed_resource.special_resource_amount = new UpgradableNumber();
  //         this.consumed_resource.special_resource_amount.base = +obj.special_resource_amount;
  //       } else if (obj && obj.special_resource_amount) {
  //         this.consumed_resource.special_resource_amount = new UpgradableNumber(obj.special_resource_amount);
  //       } else {
  //         this.consumed_resource.special_resource_amount = new UpgradableNumber();
  //       }
  //       this.consumed_resource.special_resource_refresh_rule = obj.special_resource_refresh_rule ? obj.special_resource_refresh_rule : "";
  //     }
  //     if (obj && obj.attack_bonus && obj.attack_bonus.base === undefined) {
  //       // Translate old set up to new
  //       this.attack_bonus = new UpgradableNumber();
  //       this.attack_bonus.base = +obj.attack_bonus;
  //     } else if (obj && obj.attack_bonus) {
  //       this.attack_bonus = new UpgradableNumber(obj.attack_bonus);
  //     } else {
  //       this.attack_bonus = new UpgradableNumber();
  //     }
  //     if (obj && obj.dc && obj.dc.base === undefined) {
  //       // Translate old set up to new
  //       this.dc = new UpgradableNumber();
  //       this.dc.base = +obj.dc;
  //     } else if (obj && obj.dc) {
  //       this.dc = new UpgradableNumber(obj.dc);
  //     } else {
  //       this.dc = new UpgradableNumber();
  //     }
      
  //     if (this.range === "undefined") {
  //       this.range = "";
  //     }
  //     if (this.range_2 === "undefined") {
  //       this.range_2 = "";
  //     }
  //     this.special_action_type = obj.special_action_type ? obj.special_action_type : "Inherit";
  //     this.counts_as_weapon = obj.counts_as_weapon ? obj.counts_as_weapon : "";
  //     this.weapon_properties = obj.weapon_properties ? obj.weapon_properties : { wk_ids: [], wmp_id: "" };
  //     this.show_in = obj.show_in ? obj.show_in : ["Actions"];
  //     this.special_casting_time = obj.special_casting_time ? obj.special_casting_time : "";
  //     if (!STANDARD_CASTING_TIMES.includes(this.casting_time)) {
  //       this.special_casting_time = this.casting_time;
  //       this.casting_time = "Special";
  //     }
  //     if (!obj.consumed_resource) {
  //       this.consumed_resource.recharge_die = obj.recharge_die ? obj.recharge_die : 6;
  //       this.consumed_resource.recharge_on = obj.recharge_on ? obj.recharge_on : [];
  //     }
  //     if (this.consumed_resource.resource_consumed === "Recharge" && this.consumed_resource.recharge_on.length === 0) {
  //       if (this.name.includes("(Recharge 5-6)")) {
  //         this.consumed_resource.recharge_on = [5,6];
  //         this.consumed_resource.resource_consumed = "Recharge";
  //       } else if (this.name.includes("(Recharge 4-6)")) {
  //         this.consumed_resource.recharge_on = [4,5,6];
  //         this.consumed_resource.resource_consumed = "Recharge";
  //       } else if (this.name.includes("(Recharge 6)")) {
  //         this.consumed_resource.recharge_on = [6];
  //         this.consumed_resource.resource_consumed = "Recharge";
  //       }
  //     }
  //     // if (this.description.includes("half as much")) {
  //     //   for (const effect of this.effects) {
  //     //     if (effect.type === "Damage") {
  //     //       effect.half_on_success = true;
  //     //     }
  //     //   }
  //     // }
  //     // this.spellcasting_ability = obj.spellcasting_ability ? obj.spellcasting_ability : "None";
  //   }
  // }
  
  get the_effect_types() {
    return this.effects.map(o => o.type);
  }
  
  get damage_types() {
    return this.effects.flatMap(o => o.damage_types);
  }

  get effect_types(): EffectType[] {
    return this.effects.map(o => o.type);
  }

  get the_attack_types(): AttackType[] {
    return this.effects.map(o => o.attack_type);
  }

  get the_utility_details(): string[] {
    return this.effects.filter(o => ["Control","Utility"].includes(o.type)).map(o => o.utility_detail);
  }

  get casting_times() {
    return [this.casting_time];
  }

  get resource_consumed() {
    return this.consumed_resource.resource_consumed;
  }

  toDBObj(): any {
    const db_obj = super.toDBObj();
    return {
      ...db_obj,
      // saving_throw_ability_score: this.saving_throw_ability_score,
      effects: this.effects.map(o => o.toDBObj()),
      range: this.range,
      range_2: this.range_2,
      concentration: this.concentration,
      notes: this.notes,
      duration: this.duration,
      components: this.components,
      material_component: this.material_component,
      sight: this.sight,
      casting_time: this.casting_time,
      special_casting_time: this.special_casting_time,
      consumed_resource: this.consumed_resource.toDBObj(),
      // resource_consumed: this.resource_consumed,
      // amount_consumed: this.amount_consumed,
      // slot_level: this.slot_level,
      // slot_type: this.slot_type,
      // special_resource_amount: this.special_resource_amount.toDBObj(),
      // special_resource_refresh_rule: this.special_resource_refresh_rule,
      // recharge_die: this.recharge_die,
      // recharge_on: this.recharge_on,
      attack_bonus: this.attack_bonus.toDBObj(),
      dc: this.dc.toDBObj(),
      special_action_type: this.special_action_type,
      counts_as_weapon: this.counts_as_weapon,
      weapon_properties: this.weapon_properties,
      show_in: this.show_in,
      // spellcasting_ability: this.spellcasting_ability
    };
  }

  copy(copyMe: FloatingFeature, drop_ids = false): void {
    super.copy(copyMe, drop_ids);
    if (copyMe instanceof Ability) {
      this.safe_copy(copyMe);
    }
  }

  safe_copy(copyMe: Ability): void {
    // this.saving_throw_ability_score = copyMe.saving_throw_ability_score;
    this.effects = copyMe.effects.map(e => e.clone());
    this.range = copyMe.range;
    this.range_2 = copyMe.range_2;
    this.concentration = copyMe.concentration;
    this.notes = copyMe.notes;
    this.duration = copyMe.duration;
    this.components = [...copyMe.components];
    this.material_component = copyMe.material_component;
    this.sight = copyMe.sight;
    this.casting_time = copyMe.casting_time;
    this.special_casting_time = copyMe.special_casting_time;

    this.consumed_resource = copyMe.consumed_resource.clone();
    // this.resource_consumed = copyMe.resource_consumed;
    // this.amount_consumed = copyMe.amount_consumed;
    // this.slot_level = copyMe.slot_level;
    // this.slot_type = copyMe.slot_type;
    // this.special_resource_amount = copyMe.special_resource_amount.clone();
    // this.special_resource_refresh_rule = copyMe.special_resource_refresh_rule;
    // this.recharge_die = copyMe.recharge_die;
    // this.recharge_on = [...copyMe.recharge_on];

    this.attack_bonus = copyMe.attack_bonus.clone();
    this.dc = copyMe.dc.clone();
    this.special_action_type = copyMe.special_action_type;
    this.counts_as_weapon = copyMe.counts_as_weapon;
    this.weapon_properties = {...copyMe.weapon_properties};
    this.show_in = [...copyMe.show_in];
    // this.spellcasting_ability = copyMe.spellcasting_ability;
  }

  clone(drop_ids = false): Ability {
    const new_obj = new Ability();
    new_obj.copy(this, drop_ids);
    return new_obj;
  }
}